import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { authService } from "../services/authService";

export interface User {
  id: number;
  nome: string;
  email: string;
  lojaId: number;
  roleId: number;
  isFilaSalgada: boolean;
  isFilaDoce: boolean;
  isFilaSalaoSalgada?: boolean;
  isFilaSalaoDoce?: boolean;
  isFilaRodizioSalgada?: boolean;
  isFilaRodizioDoce?: boolean;
  isOnlineIntegracao: boolean;
  isConferenteBase?: boolean;
  isSupervisorProducao?: boolean;
  isProdutor?: boolean;
  allowedScreenKeys?: string[];
  loja: {
    id: number;
    nome: string;
    endereco: string;
    telefone: string;
    cnpj: string;
    usaIntegracaoPedidos?: boolean;
    roteirizacaoAutomaticaAtiva?: boolean;
    distanciaMaximaCasamentoKm?: number;
    createdAt: string;
    updatedAt: string;
  };
  role: {
    id: number;
    nome: string;
    podeVerTodasLojas?: boolean;
    podePriorizarMontagem?: boolean;
    podeVerMaisInfoFichaProducao?: boolean;
  };
}

interface AuthContextData {
  user: User | null;
  token: string | null;
  login: (token: string, user: User) => void;
  logout: () => void;
  updateUser: (partial: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => {
    const savedUser = localStorage.getItem("user");
    const savedToken = localStorage.getItem("token");

    if (savedToken && isTokenExpired(savedToken)) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      return null;
    }

    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [token, setToken] = useState<string | null>(() => {
    const savedToken = localStorage.getItem("token");

    if (savedToken && isTokenExpired(savedToken)) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      return null;
    }

    return savedToken;
  });

  const login = (token: string, user: User) => {
    if (isTokenExpired(token)) {
      console.warn("Token já expirado");
      return;
    }

    setToken(token);
    setUser(user);
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(user));
  };

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
  }, []);

  const updateUser = useCallback((partial: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...partial };
      localStorage.setItem("user", JSON.stringify(updated));
      return updated;
    });
  }, []);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    void authService
      .fetchScreenPermissions()
      .then((allowedScreenKeys) => {
        if (!cancelled) {
          updateUser({ allowedScreenKeys });
        }
      })
      .catch(() => {
        // Backend sem a rota ainda (reiniciar API) ou indisponível: mantém
        // allowedScreenKeys indefinido para o fallback legado em canAccessPath.
      });

    return () => {
      cancelled = true;
    };
  }, [token, updateUser]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (token && isTokenExpired(token)) {
        logout();
      }
    }, 60000);

    return () => clearInterval(interval);
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth deve ser usado dentro de AuthProvider");
  }
  return context;
};
